'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { Check, X, ChevronDown, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  PRICING_PLAN_ORDER,
  PRICING_PLAN_CONTENT,
  PRICING_MARKETING_PRICES,
  PRICING_ACCESS_FEATURES,
  PRICING_TABLE_HEADERS,
  buildPricingPlanMap,
  formatNumber,
  formatPrice,
  getPricingPlanContentMap,
  getPricingUsageTableSections,
} from '@/config/pricing-content';
import { sendGAEvent } from '@/utils/analytics';
import { getPricingSpotlightState } from '@/config/site-campaigns';

// 模型 Logo 映射
const MODEL_LOGOS = {
  botgoVideo25Flash: '/brand/botgo-symbol-white.svg?v=orbit-centered-03',
  botgoVideo25_720p: '/brand/botgo-symbol-white.svg?v=orbit-centered-03',
  botgoVideo25_1080p: '/brand/botgo-symbol-white.svg?v=orbit-centered-03',
  botgoVideo25_2k: '/brand/botgo-symbol-white.svg?v=orbit-centered-03',
  botgoImage25Flash: '/brand/botgo-symbol-white.svg?v=orbit-centered-03',
  botgoImage2: '/brand/botgo-symbol-white.svg?v=orbit-centered-03',
  sora2_720p_10s: '/Logo/openai.svg',
  sora2Special_720p: '/Logo/openai.svg',
  sora2pro_720p_10s: '/Logo/openai.svg',
  sora2pro_1024p: '/Logo/openai.svg',
  sora2pro_1080p_10s: '/Logo/openai.svg',
  seedance25_480p: '/Logo/bytedance.svg',
  seedance25_720p: '/Logo/bytedance.svg',
  seedance25_1080p: '/Logo/bytedance.svg',
  geminiOmni11_360p: '/Logo/google.svg',
  geminiOmni11_720p: '/Logo/google.svg',
  geminiOmni11_1080p: '/Logo/google.svg',
  geminiOmni11_4k: '/Logo/google.svg',
  geminiOmni11Vip_360p: '/Logo/google.svg',
  geminiOmni11Vip_720p: '/Logo/google.svg',
  geminiOmni11Vip_1080p: '/Logo/google.svg',
  geminiOmni11Vip_4k: '/Logo/google.svg',
  omniFlash_720p: '/Logo/google.svg',
  omniFlash_1080p: '/Logo/google.svg',
  omniFlash_4k: '/Logo/google.svg',
  geminiOmniFlashVip_720p: '/Logo/google.svg',
  veo31FastLite_720p: '/Logo/google.svg',
  veo31FastLite_1080p: '/Logo/google.svg',
  veo31FastLite_4k: '/Logo/google.svg',
  veo31FastSpecial_720p_1080p: '/Logo/google.svg',
  veo31fast_720p: '/Logo/google.svg',
  veo31fast_1080p: '/Logo/google.svg',
  veo31fast_720p_1080p: '/Logo/google.svg',
  veo31fast_4k: '/Logo/google.svg',
  veo31_720p: '/Logo/google.svg',
  veo31_1080p: '/Logo/google.svg',
  veo31_720p_1080p: '/Logo/google.svg',
  veo31_4k: '/Logo/google.svg',
  veo31LiteExtend: '/Logo/google.svg',
  veo31FastExtend: '/Logo/google.svg',
  veo31Extend: '/Logo/google.svg',
  veo31LiteVip_720p: '/Logo/google.svg',
  veo31LiteVip_1080p: '/Logo/google.svg',
  veo31FastVip_720p: '/Logo/google.svg',
  veo31FastVip_1080p: '/Logo/google.svg',
  veo31FastVip_4k: '/Logo/google.svg',
  veo31Vip_720p: '/Logo/google.svg',
  veo31Vip_1080p: '/Logo/google.svg',
  veo31Vip_4k: '/Logo/google.svg',
  nanoBanana: '/Logo/google.svg',
  nanoBanana21_1k: '/Logo/google.svg',
  nanoBanana21_2k: '/Logo/google.svg',
  nanoBanana21_4k: '/Logo/google.svg',
  nanoBanana2Lite_1k: '/Logo/google.svg',
  nanoBanana2_1k_2k: '/Logo/google.svg',
  nanoBanana2_4k: '/Logo/google.svg',
  nanoBananaPro_1k_2k: '/Logo/google.svg',
  nanoBananaPro_4k: '/Logo/google.svg',
  seedream50Lite_2k_3k_4k: '/Logo/bytedance.svg',
  seedream50Pro_1k_2k: '/Logo/bytedance.svg',
  seedream50Pro_1k: '/Logo/bytedance.svg',
  seedream50Pro_2k: '/Logo/bytedance.svg',
  gptImage25Special_1k: '/Logo/openai.svg',
  gptImage25_1k: '/Logo/openai.svg',
  gptImage25_2k: '/Logo/openai.svg',
  gptImage25_4k: '/Logo/openai.svg',
  gptImage25Vip_1k: '/Logo/openai.svg',
  gptImage25Vip_2k: '/Logo/openai.svg',
  gptImage25Vip_4k: '/Logo/openai.svg',
  gptImage2Special_1k: '/Logo/openai.svg',
  gptImage2_1k: '/Logo/openai.svg',
  gptImage2_2k: '/Logo/openai.svg',
  gptImage2_4k: '/Logo/openai.svg',
  gptImage2Vip_1k: '/Logo/openai.svg',
  gptImage2Vip_2k: '/Logo/openai.svg',
  gptImage2Vip_4k: '/Logo/openai.svg',
  seedance15_480p: '/Logo/bytedance.svg',
  seedance15_720p: '/Logo/bytedance.svg',
  seedance15_1080p: '/Logo/bytedance.svg',
  wan30_480p: '/Logo/bailian-color.svg',
  wan30_720p: '/Logo/bailian-color.svg',
  wan30_1080p: '/Logo/bailian-color.svg',
  wan30Prime_480p: '/Logo/bailian-color.svg',
  wan30Prime_720p: '/Logo/bailian-color.svg',
  wan30Prime_1080p: '/Logo/bailian-color.svg',
  minimaxH3Special_768p: '/Logo/minimax.svg',
  minimaxH3Special_2k: '/Logo/minimax.svg',
  minimaxH3_768p: '/Logo/minimax.svg',
  minimaxH3Vip_768p: '/Logo/minimax.svg',
  minimaxH3Vip_2k: '/Logo/minimax.svg',
  minimaxH3Max_480p: '/Logo/minimax.svg',
  minimaxH3Max_768p: '/Logo/minimax.svg',
  minimaxH3_2k: '/Logo/minimax.svg',
  seedance20Mini_480p: '/Logo/bytedance.svg',
  seedance20Mini_720p: '/Logo/bytedance.svg',
  seedance20FastVip_480p: '/Logo/bytedance.svg',
  seedance20FastVip_720p: '/Logo/bytedance.svg',
  seedance20FastSpecial_720p_15s: '/Logo/bytedance.svg',
  seedance20Vip_480p: '/Logo/bytedance.svg',
  seedance20Vip_720p: '/Logo/bytedance.svg',
  seedance20Vip_1080p: '/Logo/bytedance.svg',
  seedance20Vip_4k: '/Logo/bytedance.svg',
  grokImagineSpecial_720p: '/Logo/grok.svg',
  grokImagineVideo15Special_720p: '/Logo/grok.svg',
  grokImagine_480p: '/Logo/grok.svg',
  grokImagine_720p: '/Logo/grok.svg',
  grokImagineVideo15_480p: '/Logo/grok.svg',
  grokImagineVideo15_720p: '/Logo/grok.svg',
  grokImagineVideoVip_480p: '/Logo/grok.svg',
  grokImagineVideoVip_720p: '/Logo/grok.svg',
  grokImagineVideo15Vip_480p: '/Logo/grok.svg',
  grokImagineVideo15Vip_720p: '/Logo/grok.svg',
  grokImagineVideo15Vip_1080p: '/Logo/grok.svg',
  happyhorse10_720p: '/Logo/happyhorse.png',
  happyhorse10_1080p: '/Logo/happyhorse.png',
  happyhorse_720p: '/Logo/happyhorse.png',
  happyhorse_1080p: '/Logo/happyhorse.png',
  happyhorseVideoEdit_720p: '/Logo/happyhorse.png',
  happyhorseVideoEdit_1080p: '/Logo/happyhorse.png',
  klingV4_720p: '/Logo/Kling.svg',
  klingV4_1080p: '/Logo/Kling.svg',
  klingV3_720p: '/Logo/Kling.svg',
  klingV3_1080p: '/Logo/Kling.svg',
  klingV3_720pAudio: '/Logo/Kling.svg',
  klingV3_1080pAudio: '/Logo/Kling.svg',
  klingV3_4k: '/Logo/Kling.svg',
  klingV3_4kAudio: '/Logo/Kling.svg',
  klingV3Omni_720p: '/Logo/Kling.svg',
  klingV3Omni_1080p: '/Logo/Kling.svg',
  klingV3Omni_4k: '/Logo/Kling.svg',
  klingV3Omni_720pAudio: '/Logo/Kling.svg',
  klingV3Omni_1080pAudio: '/Logo/Kling.svg',
  klingV3Omni_4kAudio: '/Logo/Kling.svg',
  klingV3Omni_720pVideoRef: '/Logo/Kling.svg',
  klingV3Omni_1080pVideoRef: '/Logo/Kling.svg',
  klingV3Omni_4kVideoRef: '/Logo/Kling.svg',
};

const BADGE_TONE_CLASSES = {
  // amber：轮廓风格，用于分辨率标签（720p / 2K / 1080p）- 参照 topview.ai 轮廓样式
  amber: 'border border-amber-500/30 bg-amber-500/10 text-amber-400',
  sky: 'border border-amber-500/30 bg-amber-500/10 text-amber-400',
  emerald: 'border border-amber-500/30 bg-amber-500/10 text-amber-400',
  violet: 'border border-amber-500/30 bg-amber-500/10 text-amber-400',
  neutral: 'border border-neutral-600 bg-neutral-800 text-neutral-300',
  // promo：amber 渐变背景 + 黑色字体，用于 365 Unlimited / 7-Day / 14-Day / Free Gens 等促销标签
  promo: 'border-0 bg-gradient-to-r from-amber-400 to-orange-500 text-black font-bold',
};

const INLINE_BADGE_PATTERN = /(?:\b365\b|\b\d+[- ]day\b|free gens?|无限)/i;

const resolveBadgeTone = (badge) => {
  const label = String(badge?.label || '').trim();
  // 明确设置了 tone 时直接使用
  if (badge?.tone) return badge.tone;
  // 内联 badge 自动识别：促销类用 promo，其他用 neutral
  if (/free gens?/i.test(label)) return 'promo';
  if (/\b\d+[- ]day\b/i.test(label)) return 'promo';
  if (/\b365\b|无限/i.test(label)) return 'promo';
  return 'neutral';
};

const renderPromoBadge = (badge, size = 'default', t = null) => {
  if (!badge?.label) return null;
  const tone = resolveBadgeTone(badge);
  const sizingClass = size === 'compact'
    ? 'px-1 py-px text-[9px] leading-[1.35]'
    : 'px-2.5 py-0.5 text-xs';

  // 优先使用翻译 key（i18n 支持）
  const displayLabel = (t && badge.labelKey && t.has(badge.labelKey))
    ? (badge.labelCount !== undefined
      ? t(badge.labelKey, { count: badge.labelCount })
      : t(badge.labelKey))
    : badge.label;

  return (
    <span
      className={`inline-flex items-center rounded font-semibold ${sizingClass} ${BADGE_TONE_CLASSES[tone] || BADGE_TONE_CLASSES.neutral}`}
    >
      {displayLabel}
    </span>
  );
};

const extractInlineBadge = (text) => {
  if (typeof text !== 'string') return null;
  const lastMarkerIndex = text.lastIndexOf(' · ');
  if (lastMarkerIndex === -1) return null;

  const badgeLabel = text.slice(lastMarkerIndex + 3).trim();
  if (!INLINE_BADGE_PATTERN.test(badgeLabel)) return null;

  return {
    text: text.slice(0, lastMarkerIndex).trim(),
    badge: {
      label: badgeLabel,
    },
  };
};

const QUALITY_TIER_LABELS = ['Low', 'Med', 'High', 'XHigh', 'Max'];
const COLLAPSED_USAGE_PREVIEW_ROW_COUNT = 5;
const USAGE_SECTION_PREVIEW_ROW_COUNT = 15;
const MIN_COLLAPSIBLE_SECTION_PREVIEW_ROW_COUNT = 4;
const PER_SECOND_PRICE_PATTERN = /^\s*([\d,]+(?:\.\d+)?)\s*(credits?|积分|積分)\s*\/\s*(second|sec|s|秒)\s*$/i;

const formatDefaultDurationPrice = (priceText, defaultDuration) => {
  const seconds = Number(defaultDuration);
  if (!Number.isFinite(seconds) || seconds <= 0 || typeof priceText !== 'string') {
    return priceText;
  }

  const match = priceText.trim().match(PER_SECOND_PRICE_PATTERN);
  if (!match) return priceText;

  const perSecondCredits = Number(match[1].replace(/,/g, ''));
  if (!Number.isFinite(perSecondCredits)) return priceText;

  const totalCredits = perSecondCredits * seconds;
  const totalText = Number.isInteger(totalCredits)
    ? formatNumber(totalCredits)
    : formatPrice(totalCredits);
  const secondsText = Number.isInteger(seconds)
    ? formatNumber(seconds)
    : formatPrice(seconds);

  return `${totalText} ${match[2]} / ${secondsText}s`;
};

const getUsageSectionPreviewRowCount = (section) => {
  const totalRows = section.rows.length;
  if (totalRows <= USAGE_SECTION_PREVIEW_ROW_COUNT) {
    return Math.max(MIN_COLLAPSIBLE_SECTION_PREVIEW_ROW_COUNT, totalRows - 2);
  }
  return USAGE_SECTION_PREVIEW_ROW_COUNT;
};

// Keep the main value centered independently of the badge. Measure the actual
// translated text so a badge moves as a whole instead of wrapping internally.
function CenteredValueWithBadge({ children, badge, className = '' }) {
  const containerRef = useRef(null);
  const valueRef = useRef(null);
  const badgeRef = useRef(null);
  const [inline, setInline] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    const value = valueRef.current;
    const badgeElement = badgeRef.current;
    const measure = () => {
      const available = container.getBoundingClientRect().width;
      const required = value.getBoundingClientRect().width + 2 * (badgeElement.getBoundingClientRect().width + 6);
      setInline(available >= required);
    };
    const observer = new ResizeObserver(measure);
    [container, value, badgeElement].forEach((element) => observer.observe(element));
    measure();
    return () => observer.disconnect();
  }, []);

  return (
    <span ref={containerRef} className={`w-full items-center gap-x-1.5 gap-y-1 ${inline ? 'grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]' : 'flex flex-col'} ${className}`}>
      <span ref={valueRef} className="col-start-2 inline-flex items-center gap-1.5 whitespace-nowrap">{children}</span>
      <span ref={badgeRef} className="col-start-3 w-max shrink-0 justify-self-start whitespace-nowrap">{badge}</span>
    </span>
  );
}

const renderCellValue = (value, unitLabel, t) => {
  if (value && typeof value === 'object' && value.type === 'unlimitedCountCell') {
    return (
      <CenteredValueWithBadge badge={renderPromoBadge(value.badge, 'compact', t)} className="text-white">
        <span className="text-base font-bold">{value.count}</span>
        {unitLabel ? <span className="text-sm text-neutral-300">{unitLabel}</span> : null}
      </CenteredValueWithBadge>
    );
  }
  if (value && typeof value === 'object' && value.type === 'discountedVideoCell') {
    return (
      <div className="flex w-full flex-col items-center gap-1 text-center leading-tight">
        {renderCellValue(value.value, unitLabel, t)}
        <CenteredValueWithBadge
          badge={renderPromoBadge({ label: t('badges.percentOff', { percent: value.discountPercent }), tone: 'promo' }, 'compact', t)}
          className="text-xs text-amber-400"
        >
          {t('tables.creditsPerFiveSeconds', { credits: formatNumber(value.credits) })}
        </CenteredValueWithBadge>
      </div>
    );
  }
  if (value && typeof value === 'object' && value.type === 'multiQuality') {
    return (
      <div className="flex flex-col items-center gap-0.5 text-white leading-tight">
        {value.values.map((v, i) => (
          <span key={i} className="inline-flex items-center justify-center gap-1 text-sm">
            <span className="font-bold">{v}</span>
            {unitLabel ? <span className="text-neutral-300"> {unitLabel}</span> : null}
            <span className="text-neutral-500 text-[10px]">({QUALITY_TIER_LABELS[i]})</span>
          </span>
        ))}
      </div>
    );
  }

  if (value && typeof value === 'object' && value.type === 'range') {
    return (
      <div className="flex flex-col items-center gap-0.5 text-white leading-tight">
        <span className="text-sm">
          <span className="font-bold">{value.min}</span>
          <span className="text-neutral-300"> {unitLabel}</span>
          <span className="text-neutral-400"> ({value.minLabel})</span>
        </span>
        <span className="text-sm">
          <span className="font-bold">{value.max}</span>
          <span className="text-neutral-300"> {unitLabel}</span>
          <span className="text-neutral-400"> ({value.maxLabel})</span>
        </span>
      </div>
    );
  }

  if (value && typeof value === 'object' && value.type === 'badge') {
    return renderPromoBadge(value, 'default', t);
  }

  if (value === 'unlimited') {
    return renderPromoBadge({ label: '365 Unlimited', tone: 'promo', labelKey: 'badges.365Unlimited' }, 'default', t);
  }

  if (value === 'x') {
    return <span className="text-neutral-400 text-lg">×</span>;
  }

  if (value && typeof value === 'object' && value.type === 'text') {
    const displayText = value.valueKey ? t(value.valueKey) : value.value;
    return <span className="text-sm font-medium text-neutral-300">{displayText}</span>;
  }

  return (
    <span className="flex items-baseline justify-center gap-1.5 text-white">
      <span className="font-bold text-base">{value}</span>
      {unitLabel ? (
        <span className="text-sm text-neutral-300">{unitLabel}</span>
      ) : null}
    </span>
  );
};

export default function PricingPlansSection({
  billingCycle,
  onBillingCycleChange,
  pricingPlansData,
  getButtonState,
  onSelectPlan,
  processingPlan,
  t,
  variant = 'page',
  showTables = true,
  promoMeta = null,
}) {
  const plansArray = useMemo(() => (pricingPlansData ? Object.values(pricingPlansData) : []), [pricingPlansData]);
  const planMap = useMemo(() => buildPricingPlanMap(plansArray), [plansArray]);
  const promoState = getPricingSpotlightState(promoMeta);
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);
  const [isUsageTableExpanded, setIsUsageTableExpanded] = useState(false);
  const [expandedUsageSections, setExpandedUsageSections] = useState({});
  const campaignActive = Boolean(promoState.isActive);
  const planContentMap = useMemo(() => getPricingPlanContentMap({
    campaignActive,
    billingCycle,
  }), [billingCycle, campaignActive]);
  const isFeatureVisibleForCycle = useCallback((item) => {
    if (item.yearlyOnly && billingCycle === 'monthly') {
      return false;
    }
    if (item.monthlyOnly && billingCycle === 'yearly') {
      return false;
    }
    return true;
  }, [billingCycle]);
  const featureSections = useMemo(() => {
    const sections = {};
    let maxTopFeatureCount = 0;

    PRICING_PLAN_ORDER.forEach((planKey) => {
      const visibleFeatures = planContentMap[planKey].features.filter(isFeatureVisibleForCycle);
      const dividerIndex = visibleFeatures.findIndex((item) => item.type === 'divider');
      const topFeatures = dividerIndex === -1 ? visibleFeatures : visibleFeatures.slice(0, dividerIndex);
      const bottomFeatures = dividerIndex === -1 ? [] : visibleFeatures.slice(dividerIndex + 1);

      sections[planKey] = {
        topFeatures,
        bottomFeatures,
      };
      maxTopFeatureCount = Math.max(maxTopFeatureCount, topFeatures.length);
    });

    return {
      sections,
      topSectionMinHeightPx: maxTopFeatureCount > 0 ? maxTopFeatureCount * 28 : 0,
    };
  }, [isFeatureVisibleForCycle, planContentMap]);
  const annualButtonLabel = promoMeta?.annualText || 'Annual';
  const annualLabel = campaignActive
    ? (promoMeta?.annualLabel || t('saveText'))
    : t('saveText');
  const showFloatingLabel = Boolean(campaignActive && promoMeta?.floatingLabel);
  const translateOrFallback = (key, fallback = '', values = undefined) => {
    if (!key) return fallback;
    if (typeof t.has === 'function' && !t.has(key)) {
      return fallback;
    }
    try {
      return values ? t(key, values) : t(key);
    } catch {
      return fallback;
    }
  };

  const getPlanDisplay = (planKey, cycle) => {
    const plan = planMap?.[planKey]?.[cycle];
    if (!plan) {
      const marketing = PRICING_MARKETING_PRICES[planKey];
      if (!marketing) return null;

      const displayPrice = cycle === 'yearly'
        ? Number(marketing.listYearlyMonthly || 0)
        : Number(marketing.listMonthly || 0);

      return {
        displayPrice,
        displayPriceText: formatPrice(displayPrice),
        yearlyTotal: cycle === 'yearly' ? displayPrice * 12 : null,
      };
    }
    const numericPrice = Number(plan.price || 0);
    const displayPrice = cycle === 'yearly' ? numericPrice / 12 : numericPrice;
    return {
      ...plan,
      displayPrice,
      displayPriceText: formatPrice(displayPrice),
      yearlyTotal: cycle === 'yearly' ? numericPrice : null,
    };
  };

  const renderFeatureItem = (planKey, item, index) => {
    if (item.type === 'label') {
      const isUnlimitedSectionLabel = item.key === 'plans.common.unlimitedGeneration';
      return (
        <div
          key={`${planKey}-label-${index}`}
          className={isUnlimitedSectionLabel
            ? 'flex items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-wide text-white'
            : 'text-[11px] font-semibold uppercase tracking-wide text-neutral-500'}
        >
          <span>{t(item.key)}</span>
          {isUnlimitedSectionLabel && (
            <button type="button" onClick={() => setGuidelinesOpen(true)} className="shrink-0 text-[10px] font-normal normal-case tracking-normal text-neutral-400 transition-colors hover:text-white">
              {t('guidelines.learnMore')}
            </button>
          )}
        </div>
      );
    }

    if (!isFeatureVisibleForCycle(item)) {
      return null;
    }

    // 获取翻译文本
    const featureText = item.labelKey ? t(item.labelKey) : item.text || t(item.key);

    // 将包含 <4K> 或 (4K) 的文本替换为划线样式
    // (<4K>) 格式: 只对 4K 划线，括号不划线
    // (4K) 格式: 整个 (4K) 划线（兼容旧格式）
    const renderTextWithStrikethrough = (text) => {
      if (typeof text !== 'string') return text;

      // 匹配 (<4K>) 格式 - 只对 4K 划线，保留括号
      const newFormatRegex = /\(<4K>\)/g;
      if (newFormatRegex.test(text)) {
        newFormatRegex.lastIndex = 0;
        const parts = text.split(/(\(<4K>\))/g);
        return parts.map((part, i) => {
          if (part === '(<4K>)') {
            return <span key={i}>(<span className="line-through">4K</span>)</span>;
          }
          return part;
        });
      }

      // 兼容旧格式：匹配 (4K) 或 （4K）
      const oldFormatRegex = /(\(4K\)|（4K）)/g;
      if (!oldFormatRegex.test(text)) return text;

      oldFormatRegex.lastIndex = 0;
      const parts = text.split(/(\(4K\)|（4K）)/g);
      return parts.map((part, i) => {
        if (part === '(4K)' || part === '（4K）') {
          return <span key={i} className="line-through">{part}</span>;
        }
        return part;
      });
    };

    // 支持 badges 数组（多标签）和单个 badge 两种模式
    const hasMultiBadges = Array.isArray(item.badges) && item.badges.length > 0;
    const parsedInlineBadge = (item.badge || hasMultiBadges) ? null : extractInlineBadge(featureText);
    const modelName = parsedInlineBadge?.text || featureText;
    const displayLines = Array.isArray(item.displayLines) && item.displayLines.length > 0
      ? item.displayLines
      : null;
    const singleBadge = item.badge || parsedInlineBadge?.badge || null;
    // 统一为 badges 数组处理
    const badges = hasMultiBadges ? item.badges : (singleBadge ? [singleBadge] : []);
    const isCompactFeature = Boolean(item.compact || (typeof item.key === 'string' && item.key.includes('.features.unlimited')));
    const checkIconClass = isCompactFeature
      ? 'mt-[3px] h-3.5 w-3.5 flex-shrink-0 text-amber-400'
      : 'mt-0.5 h-4 w-4 flex-shrink-0 text-amber-400';
    const contentClass = isCompactFeature
      ? 'flex flex-1 items-center justify-between gap-1.5 text-[11px] leading-snug text-neutral-300'
      : 'flex flex-1 items-center justify-between gap-2 text-xs leading-relaxed text-neutral-300';
    const modelClass = isCompactFeature
      ? 'min-w-0 flex items-center gap-1.5 whitespace-nowrap'
      : 'flex items-center gap-1.5';
    const badgeGroupClass = isCompactFeature
      ? 'flex flex-shrink-0 items-center gap-[5px]'
      : 'flex flex-shrink-0 items-center gap-1';
    const tooltipButtonClass = isCompactFeature
      ? 'group/info inline-flex items-center justify-center rounded-full transition-transform duration-200 hover:scale-110'
      : 'group/info inline-flex items-center justify-center rounded-full transition-transform duration-200 hover:scale-110';
    const tooltipIconClass = isCompactFeature
      ? 'h-3 w-3 text-neutral-500 transition-colors duration-200 group-hover/info:text-white group-focus-visible/info:text-white group-data-[state=delayed-open]/info:text-white'
      : 'h-3.5 w-3.5 text-neutral-500 transition-colors duration-200 group-hover/info:text-white group-focus-visible/info:text-white group-data-[state=delayed-open]/info:text-white';

    return (
      <li key={`${planKey}-${item.key || item.text}-${index}`} className="flex items-start gap-2">
        {item.disabled ? <X className={checkIconClass} /> : <Check className={checkIconClass} />}
        <span className={contentClass}>
          <span className={modelClass}>
            {displayLines ? (
              <span className="inline-flex flex-col whitespace-normal leading-[1.08]">
                {displayLines.map((line, lineIndex) => (
                  <span key={`${line}-${lineIndex}`}>{renderTextWithStrikethrough(line)}</span>
                ))}
              </span>
            ) : (
              <span>{renderTextWithStrikethrough(modelName)}</span>
            )}
            {item.tooltipKey ? (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button type="button" aria-label={t(item.tooltipKey)} className={tooltipButtonClass}>
                    <Info className={tooltipIconClass} />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="max-w-xs border border-neutral-700 bg-neutral-800 px-2.5 py-1.5 text-[11px] leading-snug text-neutral-200"
                >
                  <p>{t(item.tooltipKey)}</p>
                </TooltipContent>
              </Tooltip>
            ) : null}
          </span>
          {badges.length > 0 ? (
            <span className={badgeGroupClass}>
              {badges.map((b, i) => (
                <span key={i}>{renderPromoBadge(b, 'compact', t)}</span>
              ))}
            </span>
          ) : null}
        </span>
      </li>
    );
  };

  const usageTableSections = useMemo(() => getPricingUsageTableSections({
    billingCycle,
    campaignActive,
  }), [billingCycle, campaignActive]);
  const displayedUsageSections = useMemo(() => {
    if (!isUsageTableExpanded) {
      const firstSection = usageTableSections[0];
      if (!firstSection) return [];
      return [{
        ...firstSection,
        rows: firstSection.rows.slice(0, COLLAPSED_USAGE_PREVIEW_ROW_COUNT),
        isPreview: true,
      }];
    }

    return usageTableSections.map((section) => {
      const isSectionExpanded = Boolean(expandedUsageSections[section.key]);
      const previewRowCount = getUsageSectionPreviewRowCount(section);
      return {
        ...section,
        isExpanded: isSectionExpanded,
        rows: isSectionExpanded
          ? section.rows
          : section.rows.slice(0, previewRowCount),
      };
    });
  }, [expandedUsageSections, isUsageTableExpanded, usageTableSections]);
  const toggleUsageSection = useCallback((sectionKey) => {
    setExpandedUsageSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  }, []);
  const renderUsageModelRow = (row) => {
    const rawPriceText = row.price || translateOrFallback(`tables.rows.${row.key}.price`, '');
    const formattedPrice = formatDefaultDurationPrice(rawPriceText, row.defaultDuration);
    const fiveSecondCredits = /^(\d+(?:\.\d+)?) credits \/ 5s$/.exec(formattedPrice);
    const priceText = fiveSecondCredits
      ? t('tables.creditsPerFiveSeconds', { credits: fiveSecondCredits[1] })
      : formattedPrice;

    return (
      <tr key={row.key} className="border-b border-neutral-800/60">
        <td className="px-6 py-4 text-sm text-white text-left">
          <div className="flex items-center gap-3">
            {(MODEL_LOGOS[row.key] || (row.key.startsWith('seedance') ? '/Logo/bytedance.svg' : null)) && (
              <Image
                src={MODEL_LOGOS[row.key] || '/Logo/bytedance.svg'}
                alt=""
                width={20}
                height={20}
                className={`flex-shrink-0 ${row.key.startsWith('grokImagine') ? 'invert brightness-0 invert' : ''}`}
                style={{
                  width: '20px',
                  height: '20px',
                  ...(row.key.startsWith('grokImagine') ? { filter: 'brightness(0) invert(1)' } : {}),
                }}
              />
            )}
            <div className="flex min-w-0 flex-col">
              <span className="inline-flex items-center gap-1 font-medium">
                {row.name || translateOrFallback(`tables.rows.${row.key}.name`, row.key)}
                {row.tooltipKey && (
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <button type="button" aria-label={t(row.tooltipKey)} className="text-neutral-500 hover:text-neutral-200">
                        <Info className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs border border-neutral-700 bg-neutral-900 text-white">
                      {t(row.tooltipKey)}
                    </TooltipContent>
                  </Tooltip>
                )}
              </span>
              {row.privacyMode && (
                <span className="text-xs text-neutral-400">{t(`tables.privacyModes.${row.privacyMode}`)}</span>
              )}
              <span className="text-sm text-neutral-400 whitespace-pre-line">
                {row.priceTiers ? row.priceTiers.map(({ quality, credits, standardCredits, discountPercent }) => (
                  <span key={quality} className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                    {discountPercent > 0 && <del className="text-neutral-500">{standardCredits}</del>}
                    <span>{t('tables.imageQualityPrice', { credits, quality: t(`tables.qualityLabels.${quality}`) })}</span>
                    {discountPercent > 0 && renderPromoBadge({ label: t('badges.percentOff', { percent: discountPercent }), tone: 'promo' }, 'compact')}
                  </span>
                )) : row.promoPrice ? (
                  <span className="flex flex-wrap items-center gap-1.5">
                    <del className="text-neutral-500">{formatNumber(row.promoPrice.original)}</del>
                    <span>{t('tables.creditsPerFiveSeconds', { credits: formatNumber(row.promoPrice.current) })}</span>
                    {renderPromoBadge({ label: t('badges.percentOff', { percent: row.promoPrice.discountPercent }), tone: 'promo' }, 'compact')}
                  </span>
                ) : priceText}
              </span>
            </div>
          </div>
        </td>
        {PRICING_TABLE_HEADERS.map((planKey) => (
          <td key={`${row.key}-${planKey}`} className="px-4 py-4 text-center">
            {renderCellValue(row.cells[planKey], t(`tables.units.${row.unit || 'videos'}`), t)}
          </td>
        ))}
      </tr>
    );
  };
  const renderUsageSectionHeader = (section) => (
    <tr key={`${section.key}-header`} className="border-b border-neutral-800 bg-neutral-800/80">
      <td colSpan={PRICING_TABLE_HEADERS.length + 1} className="px-6 py-3 text-left text-sm font-semibold text-white">
        {translateOrFallback(`tables.sections.${section.key}`, section.title)}
      </td>
    </tr>
  );
  const renderUsageSectionToggleRow = (section) => {
    const totalRows = usageTableSections.find((item) => item.key === section.key)?.rows.length || 0;
    const canToggleRows = section.isExpanded || totalRows > section.rows.length;
    if (!isUsageTableExpanded || !canToggleRows) return null;

    const labelKey = section.isExpanded ? 'tables.viewLess' : 'tables.viewMore';
    const fallback = section.isExpanded ? 'View Less' : 'View More';

    return (
      <tr key={`${section.key}-toggle`} className="border-b border-neutral-800/60">
        <td colSpan={PRICING_TABLE_HEADERS.length + 1} className="px-6 py-4 text-left">
          <button
            type="button"
            onClick={() => toggleUsageSection(section.key)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-300 transition-colors hover:text-white"
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${section.isExpanded ? 'rotate-180' : ''}`} />
            {translateOrFallback(labelKey, fallback)}
          </button>
        </td>
      </tr>
    );
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className={variant === 'modal' ? 'px-8 pb-10' : ''}>
        {/* Billing Cycle Toggle */}
        <div className={variant === 'modal' ? 'mb-8 flex justify-center' : 'mb-12 flex justify-center'}>
          <div className="relative inline-flex">
            {showFloatingLabel ? (
              <span className="pointer-events-none absolute -top-4 right-1 z-20 rounded-md bg-amber-400 px-2.5 py-1 text-[11px] font-bold whitespace-nowrap text-black shadow-[0_14px_24px_rgba(245,158,11,0.18)] sm:-right-2">
                {promoMeta.floatingLabel}
              </span>
            ) : null}

            <div className="inline-flex rounded-full border border-neutral-700 bg-neutral-800/50 p-1 backdrop-blur-sm">
              <button
                onClick={() => onBillingCycleChange('monthly')}
                className={`rounded-full px-7 py-2.5 text-sm font-semibold transition-all ${billingCycle === 'monthly'
                  ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg'
                  : 'text-neutral-400 hover:text-white'
                  }`}
              >
                {t('monthly')}
              </button>
              <button
                onClick={() => onBillingCycleChange('yearly')}
                className={`flex items-center gap-2 rounded-full px-7 py-2.5 text-sm font-semibold transition-all ${billingCycle === 'yearly'
                  ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg'
                  : 'text-neutral-400 hover:text-white'
                  }`}
              >
                {annualButtonLabel}
                <span className="shrink-0 whitespace-nowrap rounded-full bg-amber-500/20 px-2 py-0.5 text-sm font-semibold text-amber-200">
                  {annualLabel}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 justify-center gap-4 md:grid-cols-2 lg:grid-cols-4">
          {PRICING_PLAN_ORDER.map((planKey) => {
            const content = planContentMap[planKey];
            const { topFeatures = [], bottomFeatures = [] } = featureSections.sections[planKey] || {};
            const displayPlan = getPlanDisplay(planKey, billingCycle) || {};
            const marketing = PRICING_MARKETING_PRICES[planKey];
            const buttonState = getButtonState(planKey);
            const isProcessing = processingPlan === planKey;
            const isDisabled = buttonState.disabled || isProcessing;
            const listPrice = marketing
              ? billingCycle === 'yearly'
                ? marketing.listYearlyMonthly
                : marketing.listMonthly
              : null;
            const showSaveBadge = billingCycle === 'yearly' && marketing?.savePercentYearly;
            const hasListPrice = listPrice && Number(displayPlan.displayPrice || 0) < listPrice;
            // 原价积分（用于划线显示）
            const originalCredits = {
              plus: 1500,
              pro: 3000,
              max: 6000,
              ultra: 20000,
            };
            const origCredits = originalCredits[planKey] || 0;

            // 按钮是否需要显示 tooltip（禁用但不是 loading/processing 状态）
            const showDisabledTooltip = isDisabled &&
              !isProcessing &&
              (buttonState.action === 'current' ||
                buttonState.action === 'downgrade_blocked' ||
                buttonState.action === 'none');

            // 按钮样式基础类（font-bold 加粗文字）
            const baseButtonClass = 'w-full py-5 font-bold transition-all font-playfair';
            // 禁用状态按钮样式：深灰背景 + 浅灰文字，!opacity-100 覆盖 shadcn disabled:opacity-50
            const disabledButtonClass = `${baseButtonClass} bg-neutral-800 text-neutral-500 border-0 !opacity-100`;
            // Ultra（popular）可升级样式：字体黑色（在 amber 渐变背景上更清晰）
            const popularButtonClass = `${baseButtonClass} bg-gradient-to-r from-amber-500 to-orange-600 text-black hover:from-amber-600 hover:to-orange-700 hover:shadow-lg hover:shadow-amber-500/20`;
            // Free/Plus/Pro 可升级样式：白底黑字
            const normalButtonClass = `${baseButtonClass} bg-white text-black hover:bg-neutral-200 active:bg-neutral-100`;

            return (
              <div
                key={planKey}
                className={`relative mx-auto flex h-full w-full max-w-[318px] flex-col rounded-2xl border bg-neutral-800/50 p-6 backdrop-blur-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-xl hover:shadow-black/30 ${content.isPopular || content.isBestValue
                  ? 'border-amber-500 shadow-amber-500/20 hover:border-amber-400'
                  : 'border-neutral-700 hover:border-neutral-400'
                  }`}
              >
                {content.isPopular || content.isBestValue ? (
                  <div className="absolute -top-4 left-6">
                    <div className="rounded-full bg-gradient-to-r from-amber-500 to-orange-600 px-4 py-1 text-sm font-semibold text-white shadow-lg">
                      {content.isBestValue ? 'Best Value' : t('mostPopular')}
                    </div>
                  </div>
                ) : null}

                <div className="mb-4 flex items-center gap-2">
                  <h3 className="text-2xl font-bold text-white font-playfair">
                    {t(content.nameKey)}
                  </h3>
                  {showSaveBadge ? (
                    <span className="rounded-md bg-amber-500 px-2.5 py-1 text-[11px] font-semibold text-white">
                      {t('savePercent', { percent: marketing.savePercentYearly })}
                    </span>
                  ) : null}
                </div>

                <div className="mb-4">
                  <div className="flex items-end gap-2">
                    {planKey === 'free' ? (
                      <span className="text-4xl font-bold text-white font-playfair">{t(content.nameKey)}</span>
                    ) : (
                      <>
                        <span className="text-4xl font-bold text-white font-playfair">
                          ${displayPlan.displayPriceText || '0'}
                        </span>
                        <span className="pb-1 text-sm text-neutral-400">{t('month')}</span>
                        {hasListPrice ? (
                          <span className="pb-1 text-sm text-neutral-500 line-through">
                            ${formatPrice(listPrice)}
                          </span>
                        ) : null}
                      </>
                    )}
                  </div>
                  <div className="mt-2 min-h-[20px] text-sm text-neutral-400">
                    {billingCycle === 'yearly' && planKey !== 'free' && displayPlan.yearlyTotal ? (
                      <span>
                        {t('billedAnnuallyTotal', { total: formatNumber(displayPlan.yearlyTotal) })}
                      </span>
                    ) : null}
                  </div>
                  <div className="min-h-[20px] text-sm font-semibold text-amber-400">
                    {planKey === 'free' ? (
                      t(content.descriptionKey)
                    ) : (
                      <span>
                        {formatNumber(displayPlan.credits || 0)}{' '}
                        <span className="text-neutral-500 line-through">{formatNumber(origCredits)}</span>{' '}
                        {t('creditsMonthlyLabel')}
                      </span>
                    )}
                  </div>
                </div>

                {/* 按钮：位于积分说明文案下方、功能列表上方 */}
                {showDisabledTooltip ? (
                  // 禁用状态 + Tooltip：cursor 放在 div 上（因 shadcn Button disabled:pointer-events-none）
                  // TooltipContent z-[200] 确保在 PricingModal（z-[120]）上方显示
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="w-full mb-4 cursor-not-allowed">
                        <Button
                          disabled
                          className={disabledButtonClass}
                        >
                          {buttonState.text}
                        </Button>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="z-[200] border border-neutral-700 bg-neutral-800 text-sm text-neutral-200 whitespace-nowrap"
                    >
                      {t('notAvailableForUpgrade')}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  // 正常/loading 状态
                  <div className={`w-full mb-4 ${isDisabled ? 'cursor-not-allowed' : ''}`}>
                    <Button
                      onClick={() => {
                        if (!isDisabled) {
                          sendGAEvent('click_upgrade', {
                            plan_key: planKey,
                            billing_cycle: billingCycle,
                          });
                          onSelectPlan(planKey);
                        }
                      }}
                      disabled={isDisabled}
                      className={
                        isDisabled
                          ? disabledButtonClass
                          : (content.isPopular || content.isBestValue)
                            ? popularButtonClass
                            : normalButtonClass
                      }
                    >
                      {isProcessing ? t('processing') : buttonState.text}
                    </Button>
                  </div>
                )}

                {/* 分割线 */}
                <div className="mb-4 h-px w-full bg-neutral-700/60" />

                <div className="flex flex-1 flex-col">
                  <ul
                    className="flex flex-col gap-2 lg:min-h-[var(--pricing-top-feature-height)]"
                    style={featureSections.topSectionMinHeightPx > 0
                      ? { '--pricing-top-feature-height': `${featureSections.topSectionMinHeightPx}px` }
                      : undefined}
                  >
                    {topFeatures.map((item, index) => renderFeatureItem(planKey, item, index))}
                  </ul>

                  {bottomFeatures.length > 0 ? (
                    <div className="mt-4">
                      <div className="h-px w-full bg-neutral-700/60" />
                      <ul className="mt-5 flex flex-col gap-2">
                        {bottomFeatures.map((item, index) => renderFeatureItem(planKey, item, index + topFeatures.length))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        {showTables ? (
          <div className={variant === 'modal'
            ? 'mt-12'
            : 'relative left-1/2 mt-20 w-[calc(100vw-2rem)] max-w-[1540px] -translate-x-1/2'}>
            <div className="mb-6 text-center">
              <h3 className="text-2xl font-bold text-white font-playfair">
                {t('tables.title')}
              </h3>
              <p className="mt-2 text-sm text-neutral-400">{t('tables.subtitle')}</p>
            </div>

            {/* Monthly/Yearly 切换按钮 - 放在表格外部 */}
            <div className="flex items-center justify-center mb-6">
              <div className="inline-flex rounded-full border border-neutral-700 bg-neutral-800/50 p-1 backdrop-blur-sm">
                <button
                  onClick={() => onBillingCycleChange('monthly')}
                  className={`rounded-full px-7 py-2.5 text-sm font-semibold transition-all ${billingCycle === 'monthly'
                    ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg'
                    : 'text-neutral-400 hover:text-white'
                    }`}
                >
                  {t('monthly')}
                </button>
                <button
                  onClick={() => onBillingCycleChange('yearly')}
                  className={`flex items-center gap-2 rounded-full px-7 py-2.5 text-sm font-semibold transition-all ${billingCycle === 'yearly'
                    ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg'
                  : 'text-neutral-400 hover:text-white'
                  }`}
                >
                  {annualButtonLabel}
                  <span className="shrink-0 whitespace-nowrap rounded-full bg-amber-500/20 px-2 py-0.5 text-sm font-semibold text-amber-300">
                    {annualLabel}
                  </span>
                </button>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-neutral-700 bg-neutral-900/40">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1100px] table-fixed">
                  <colgroup>
                    <col className="w-[22.5%]" />
                    <col className="w-[19.375%]" />
                    <col className="w-[19.375%]" />
                    <col className="w-[19.375%]" />
                    <col className="w-[19.375%]" />
                  </colgroup>
                  <thead className="bg-neutral-900/80">
                    <tr className="border-b border-neutral-800">
                      <th className="px-6 py-4 text-sm font-semibold text-neutral-300 text-left">
                        {t('tables.headers.models')}
                      </th>
                      {PRICING_TABLE_HEADERS.map((planKey) => {
                        const headerPlan = getPlanDisplay(planKey, billingCycle) || {};
                        const planName = t(PRICING_PLAN_CONTENT[planKey].nameKey);
                        const priceLabel = planKey === 'free'
                          ? t('tables.freeLabel')
                          : `$${formatPrice(headerPlan.displayPrice || 0)}/${t('month').replace('/', '')}`;
                        const creditsLabel = planKey === 'free'
                          ? t('tables.freeCredits')
                          : t('tables.creditsLabel', { credits: formatNumber(headerPlan.credits || 0) });
                        return (
                          <th key={planKey} className="px-4 py-4 text-center">
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-base font-medium text-white">{planName}</span>
                              <span className="text-sm font-normal text-neutral-300">{priceLabel}</span>
                              <span className="text-sm font-normal text-neutral-400">{creditsLabel}</span>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {displayedUsageSections.flatMap((section) => [
                      renderUsageSectionHeader(section),
                      ...section.rows.map((row) => renderUsageModelRow(row)),
                      renderUsageSectionToggleRow(section),
                    ].filter(Boolean))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-5 flex justify-center">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsUsageTableExpanded((value) => !value)}
                className="min-w-[190px] border-neutral-700 bg-neutral-900/80 text-neutral-200 transition-all hover:scale-[1.02] hover:bg-neutral-800 hover:text-white"
              >
                {translateOrFallback('tables.compareFeatures', 'Compare Features')}
                <ChevronDown className={`ml-2 h-4 w-4 transition-transform ${isUsageTableExpanded ? 'rotate-180' : ''}`} />
              </Button>
            </div>

            {isUsageTableExpanded ? (
              <div className="mt-10">
              <h3 className="mb-4 text-2xl font-bold text-white font-playfair">{t('tables.accessTitle')}</h3>
              <div className="overflow-hidden rounded-2xl border border-neutral-700 bg-neutral-900/40">
                <div className="overflow-x-auto">
                  <table className="w-full table-fixed">
                    <colgroup>
                      <col className="w-[22.5%]" />
                      <col className="w-[19.375%]" />
                      <col className="w-[19.375%]" />
                      <col className="w-[19.375%]" />
                      <col className="w-[19.375%]" />
                    </colgroup>
                    <thead className="bg-neutral-900/80">
                      <tr className="border-b border-neutral-800">
                        <th className="px-6 py-4 text-sm font-semibold text-white text-left">
                          {t('tables.headers.features')}
                        </th>
                        {PRICING_TABLE_HEADERS.map((planKey) => (
                          <th key={planKey} className="px-4 py-4 text-center text-base font-medium text-white">
                            {t(PRICING_PLAN_CONTENT[planKey].nameKey)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {PRICING_ACCESS_FEATURES.map((row) => (
                        <tr key={row.key} className="border-b border-neutral-800/60">
                          <td className="px-6 py-4 text-sm text-white font-normal text-left">
                            {t(`tables.accessRows.${row.key}`)}
                          </td>
                          {PRICING_TABLE_HEADERS.map((planKey) => {
                            const value = row.cells[planKey];
                            return (
                              <td key={`${row.key}-${planKey}`} className="px-4 py-4 text-center">
                                {typeof value === 'boolean' ? (
                                  <span className={`text-lg ${value ? 'text-amber-400' : 'text-neutral-400'}`}>
                                    {value ? '✓' : '×'}
                                  </span>
                                ) : (
                                  <span className="text-base font-medium text-white">{value}</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            ) : null}
          </div>
        ) : null}
      </div>
      <Dialog open={guidelinesOpen} onOpenChange={setGuidelinesOpen}>
        <DialogContent className="z-[220] max-h-[85dvh] w-[calc(100%-2rem)] max-w-2xl overflow-y-auto border-white/10 bg-[#201b17] p-6 text-neutral-200 sm:p-8" overlayClassName="z-[219]">
          <DialogTitle className="pr-5 text-lg">{t('guidelines.title')}</DialogTitle>
          <DialogDescription className="text-sm leading-6 text-neutral-400">{t('guidelines.intro')}</DialogDescription>
          {['includes', 'period', 'speed', 'concurrency', 'fairUse'].map((key) => (
            <section key={key} className="space-y-2">
              <h3 className="text-sm font-semibold">{t(`guidelines.${key}.title`)}</h3>
              <p className="text-sm leading-6 text-neutral-400">{t(`guidelines.${key}.body`)}</p>
            </section>
          ))}
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
